import React, { Component } from 'react'
import {
    Card, Table, Button, Select, Tag, Space, Alert,
    Popconfirm, message, Typography, Tooltip
} from 'antd'
import {
    getApiCredentialList, createApiCredential,
    refreshApiCredential, renewApiCredentialAutoLogin, revokeApiCredentialAutoLogin,
    getUserListForDropbox
} from '../../request'
import {
    AppleOutlined, CheckCircleOutlined,
    StopOutlined, SyncOutlined, WindowsOutlined
} from '@ant-design/icons'
import moment from 'moment'
import {
    MAC_INSTALLER_ARCHIVE_NAME,
    WINDOWS_INSTALLER_NAME,
    buildMacInstallerArchive,
    buildWindowsInstallerScript,
} from './apiKeyEnvironmentInstaller'

const { Text } = Typography
const NEW_KEY_CACHE_KEY = 'portal_api_key_latest_full_key'
const AUTO_LOGIN_DEFAULT_DAYS = 30

/**
 * API Key 管理
 * onlyMine: 为 true 时仅展示当前用户自己的 key，隐藏用户筛选器（个人中心场景）
 * fixedUserId: 锁定用户ID（onlyMine=true 时由父组件传入）
 *
 * 后端接口约定：
 *   - userId 为空  → 查询全部（管理员场景，后端需校验调用方具有管理员权限）
 *   - userId 有值  → 按该 userId 过滤（后端需校验调用方是该用户本人或管理员）
 */
export default class ApiKeyManagement extends Component {
    autoCreateInFlight = false

    state = {
        dataSource: [],
        userList: [],
        columns: [],
        loading: false,
        creating: false,
        newKeyVisible: false,
        newKeyValue: '',
        newKeyRecordId: null,
        formParam: {
            currentPage: 1,
            pageSize: 20,
        },
        total: 0,
    }

    componentDidMount() {
        const { fixedUserId } = this.props
        // columns 依赖 onlyMine prop，初始化一次存入 state，避免每次 render 重建
        this.restoreNewKeyCache()
        this.setState({ columns: this.buildColumns() })
        if (fixedUserId) {
            this.setState(
                { formParam: { ...this.state.formParam, userId: fixedUserId } },
                () => this.initList()
            )
        } else {
            this.initList()
            this.initUserList()
        }
    }

    shouldAutoCreateOwnKey = () => {
        const { onlyMine, fixedUserId } = this.props
        return !!(onlyMine && fixedUserId)
    }

    restoreNewKeyCache = () => {
        try {
            const cache = sessionStorage.getItem(NEW_KEY_CACHE_KEY)
            if (!cache) return
            const parsed = JSON.parse(cache)
            if (!parsed?.fullKey || !parsed?.recordId) return
            this.setState({
                newKeyVisible: true,
                newKeyValue: parsed.fullKey,
                newKeyRecordId: parsed.recordId,
            })
        } catch (e) {}
    }

    persistNewKeyCache = (recordId, fullKey) => {
        try {
            sessionStorage.setItem(NEW_KEY_CACHE_KEY, JSON.stringify({ recordId, fullKey }))
        } catch (e) {}
    }


    normalizeListResponse = (res) => {
        if (Array.isArray(res)) {
            return { list: res, total: res.length }
        }
        if (Array.isArray(res?.list)) {
            return { list: res.list, total: res.total || res.list.length }
        }
        if (Array.isArray(res?.records)) {
            return { list: res.records, total: res.total || res.records.length }
        }
        if (Array.isArray(res?.data)) {
            return { list: res.data, total: res.total || res.data.length }
        }
        if (Array.isArray(res?.data?.list)) {
            return { list: res.data.list, total: res.data.total || res.total || res.data.list.length }
        }
        if (Array.isArray(res?.data?.records)) {
            return { list: res.data.records, total: res.data.total || res.total || res.data.records.length }
        }
        return { list: [], total: 0 }
    }

    initList = async () => {
        this.setState({ loading: true })
        try {
            const res = await getApiCredentialList(this.state.formParam)
            const { list, total } = this.normalizeListResponse(res)
            if (this.shouldAutoCreateOwnKey() && total === 0) {
                const created = await this.ensureOwnKeyIfMissing()
                if (created) {
                    const latestRes = await getApiCredentialList(this.state.formParam)
                    const latest = this.normalizeListResponse(latestRes)
                    this.setState({
                        loading: false,
                        dataSource: latest.list,
                        total: latest.total,
                    })
                    return
                }
            }
            this.setState({
                loading: false,
                dataSource: list,
                total,
            })
        } catch (e) {
            this.setState({ loading: false })
        }
    }

    initUserList = () => {
        getUserListForDropbox({ type: 0 }).then(res => {
            this.setState({ userList: res || [] })
        })
    }

    ensureOwnKeyIfMissing = async () => {
        if (!this.shouldAutoCreateOwnKey() || this.autoCreateInFlight) {
            return false
        }

        this.autoCreateInFlight = true
        this.setState({ creating: true })
        try {
            const res = await createApiCredential({
                name: 'apikey',
                scopes: ['api:*'],
            })
            const fullKey = res?.fullKey || res?.data?.fullKey || ''
            const recordId = res?.id || res?.data?.id || null
            this.setState({
                newKeyValue: fullKey,
                newKeyRecordId: recordId,
                newKeyVisible: !!fullKey,
            })
            if (fullKey && recordId) {
                this.persistNewKeyCache(recordId, fullKey)
                message.success('已自动创建 API Key，请立即保存完整 Key')
            }
            return true
        } catch (e) {
            return false
        } finally {
            this.autoCreateInFlight = false
            this.setState({ creating: false })
        }
    }

    copyText = async (text, successMessage = '已复制到剪贴板') => {
        if (!text) {
            message.warning('当前没有可复制的完整 Key')
            return
        }
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(text)
            } else {
                const input = document.createElement('input')
                input.value = text
                document.body.appendChild(input)
                input.select()
                document.execCommand('copy')
                document.body.removeChild(input)
            }
            message.success(successMessage)
        } catch (e) {
            message.error('复制失败，请手动复制')
        }
    }

    getRecordFullKey = (record = {}) => {
        const { newKeyRecordId, newKeyValue } = this.state
        if (record.fullKey) return record.fullKey
        if (String(record.id) === String(newKeyRecordId) && newKeyValue) return newKeyValue
        return ''
    }

    copyFullKey = async () => {
        await this.copyText(this.state.newKeyValue, '完整 Key 已复制')
    }

    copyRecordFullKey = async (record) => {
        await this.copyText(this.getRecordFullKey(record), '完整 Key 已复制')
    }

    getAvailableFullKey = () => {
        const { dataSource, newKeyValue } = this.state
        if (newKeyValue) {
            return newKeyValue
        }
        const recordWithFullKey = dataSource.find(record => this.getRecordFullKey(record))
        return recordWithFullKey ? this.getRecordFullKey(recordWithFullKey) : ''
    }

    downloadBlob = (blob, fileName) => {
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.setTimeout(() => window.URL.revokeObjectURL(url), 0)
    }

    handleDownloadEnvironmentInstaller = async (platform, fullKey) => {
        if (!fullKey) {
            message.warning('当前没有可用于生成安装脚本的完整 Key，请先刷新 Key')
            return
        }

        try {
            if (platform === 'mac') {
                const archive = await buildMacInstallerArchive(fullKey)
                this.downloadBlob(archive, MAC_INSTALLER_ARCHIVE_NAME)
                message.success('Mac 配置包已下载：解压后双击执行，完成后重启 Codex，并删除下载文件')
                return
            }

            const script = buildWindowsInstallerScript(fullKey)
            this.downloadBlob(
                new Blob([script], { type: 'application/octet-stream' }),
                WINDOWS_INSTALLER_NAME
            )
            message.success('Windows 配置脚本已下载：双击执行，完成后重启 Codex，并删除下载文件')
        } catch (e) {
            message.error('环境配置脚本生成失败，请联系技术人员')
        }
    }

    renderEnvironmentInstallerSection = (fullKey) => {
        if (!this.props.onlyMine) {
            return null
        }

        const downloadDisabled = !fullKey
        return (
            <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 20, paddingTop: 16 }}>
                <div style={{ marginBottom: 16 }}>
                    <Text strong>一键配置 API Key 环境变量</Text>
                    <div style={{ marginTop: 4 }}>
                        <Text type="secondary">选择当前电脑系统。执行成功后，完全退出并重新打开 Codex。</Text>
                    </div>
                </div>

                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                        gap: 24,
                        maxWidth: 760,
                    }}
                >
                    <div>
                        <Button
                            type="primary"
                            icon={<AppleOutlined />}
                            disabled={downloadDisabled}
                            onClick={() => this.handleDownloadEnvironmentInstaller('mac', fullKey)}
                        >
                            下载 Mac 版
                        </Button>
                        <div style={{ marginTop: 8 }}>
                            <Text type="secondary">下载 ZIP，解压后双击执行即可。</Text>
                        </div>
                    </div>

                    <div>
                        <Button
                            type="primary"
                            icon={<WindowsOutlined />}
                            disabled={downloadDisabled}
                            onClick={() => this.handleDownloadEnvironmentInstaller('windows', fullKey)}
                        >
                            下载 Windows 版
                        </Button>
                        <div style={{ marginTop: 8 }}>
                            <Text type="secondary">下载后直接双击执行即可。</Text>
                        </div>
                    </div>
                </div>

                {downloadDisabled ? (
                    <Alert
                        type="warning"
                        showIcon
                        message="请先点击表格中的“刷新Key”获取完整 Key；刷新后旧 Key 会立即失效。"
                        style={{ marginTop: 16 }}
                    />
                ) : (
                    <div style={{ marginTop: 16 }}>
                        <Text type="danger">
                            下载文件包含当前 API Key，仅限本人电脑使用；配置完成后请删除下载文件。
                        </Text>
                    </div>
                )}
            </div>
        )
    }

    handleRefresh = (record) => {
        this.setState({ loading: true })
        refreshApiCredential({ id: record.id })
            .then(res => {
                const fullKey = res?.fullKey || res?.data?.fullKey || ''
                const recordId = res?.id || res?.data?.id || null
                message.success('已刷新 API Key')
                this.setState({
                    loading: false,
                    newKeyValue: fullKey,
                    newKeyRecordId: recordId,
                    newKeyVisible: true,
                })
                if (fullKey && recordId) this.persistNewKeyCache(recordId, fullKey)
                this.initList()
            })
            .catch(() => this.setState({ loading: false }))
    }

    handleRenewAutoLogin = (record) => {
        this.setState({ loading: true })
        const days = record?.autoLoginDefaultDays || AUTO_LOGIN_DEFAULT_DAYS
        renewApiCredentialAutoLogin({ id: record.id })
            .then(() => {
                message.success(`免登录已续期${days}天`)
                this.initList()
            })
            .catch(() => this.setState({ loading: false }))
    }

    handleRevokeAutoLogin = (record) => {
        this.setState({ loading: true })
        revokeApiCredentialAutoLogin({ id: record.id })
            .then(() => {
                message.success('免登录已失效，所有设备需重新登录')
                this.initList()
            })
            .catch(() => this.setState({ loading: false }))
    }

    isAutoLoginActive = (record) => {
        const autoLoginExpireAt = record?.autoLoginExpireAt
        if (!autoLoginExpireAt) {
            return false
        }
        const expireMoment = moment(autoLoginExpireAt)
        return expireMoment.isValid() && !expireMoment.isBefore(moment())
    }

    renderAutoLoginStatus = (record) => {
        const autoLoginExpireAt = record?.autoLoginExpireAt
        if (!autoLoginExpireAt) {
            return <Tag>未开启</Tag>
        }
        const expireMoment = moment(autoLoginExpireAt)
        if (!expireMoment.isValid()) {
            return <Tag>未开启</Tag>
        }
        if (expireMoment.isBefore(moment())) {
            return <Tag color="default">已失效</Tag>
        }
        return <Tag color="success">生效中 · 至 {expireMoment.format('YYYY-MM-DD HH:mm:ss')}</Tag>
    }

    buildColumns = () => {
        const { onlyMine } = this.props
        const cols = [
            {
                title: '名称',
                dataIndex: 'name',
                key: 'name',
                width: 150,
            },
            {
                title: 'Key前缀',
                dataIndex: 'keyDisplay',
                key: 'keyDisplay',
                width: 160,
                render: (text) => <Text code>{text}</Text>,
            },
            {
                title: '状态',
                dataIndex: 'status',
                key: 'status',
                width: 90,
                render: (status) =>
                    status === 1
                        ? <Tag color="success" icon={<CheckCircleOutlined />}>启用</Tag>
                        : <Tag color="default" icon={<StopOutlined />}>禁用</Tag>,
            },
            {
                title: '最近使用',
                dataIndex: 'lastUsedAt',
                key: 'lastUsedAt',
                width: 170,
                render: (t) => t ? moment(t).format('YYYY-MM-DD HH:mm:ss') : '-',
            },
            {
                title: '过期时间',
                dataIndex: 'expireAt',
                key: 'expireAt',
                width: 170,
                render: (t) => t ? moment(t).format('YYYY-MM-DD HH:mm:ss') : '永不过期',
            },
            {
                title: '免登录',
                key: 'autoLoginStatus',
                width: 220,
                render: (_, record) => this.renderAutoLoginStatus(record),
            },
            {
                title: '创建时间',
                dataIndex: 'createdAt',
                key: 'createdAt',
                width: 170,
                render: (t) => t ? moment(t).format('YYYY-MM-DD HH:mm:ss') : '-',
            },
        ]
        if (!onlyMine) {
            cols.splice(2, 0, {
                title: '所属用户',
                dataIndex: 'userNick',
                key: 'userNick',
                width: 120,
            })
        }
        cols.push({
            title: '免登录操作',
            key: 'autoLoginActions',
            width: 240,
            render: (_, record) => {
                const renewDays = record?.autoLoginDefaultDays || AUTO_LOGIN_DEFAULT_DAYS
                const autoLoginActive = this.isAutoLoginActive(record)
                return (
                    <Space size={4}>
                        <Popconfirm
                            title={autoLoginActive ? `确定将免登录有效期延长 ${renewDays} 天吗？` : `确定开启免登录 ${renewDays} 天吗？`}
                            onConfirm={() => this.handleRenewAutoLogin(record)}
                            okText="确定"
                            cancelText="取消"
                        >
                            <Button size="small">{autoLoginActive ? `续期${renewDays}天` : `开启${renewDays}天`}</Button>
                        </Popconfirm>
                        {autoLoginActive ? (
                            <Popconfirm
                                title="确定让所有设备上的免登录立即失效吗？"
                                onConfirm={() => this.handleRevokeAutoLogin(record)}
                                okText="确定"
                                cancelText="取消"
                            >
                                <Button size="small" danger>立即失效</Button>
                            </Popconfirm>
                        ) : null}
                    </Space>
                )
            },
        })
        cols.push({
            title: 'API Key操作',
            key: 'credentialActions',
            fixed: 'right',
            width: 220,
            render: (_, record) => {
                const recordFullKey = this.getRecordFullKey(record)
                return (
                    <Space size={4}>
                        {recordFullKey ? (
                            <Button size="small" type="primary" onClick={() => this.copyRecordFullKey(record)}>
                                复制完整Key
                            </Button>
                        ) : onlyMine ? (
                            <Tooltip title="完整 Key 只在创建或刷新后显示，请先刷新 Key">
                                <span>
                                    <Button size="small" type="primary" disabled>
                                        复制完整Key
                                    </Button>
                                </span>
                            </Tooltip>
                        ) : null}
                        <Popconfirm 
                            title="确定刷新 API Key 吗？刷新后旧 Key 将立即失效"
                            onConfirm={() => this.handleRefresh(record)}
                            okText="确定"
                            cancelText="取消"
                        >
                            <Button size="small" icon={<SyncOutlined />}>刷新Key</Button>
                        </Popconfirm>
                    </Space>
                )
            },
        })
        return cols
    }

    render() {
        const { onlyMine } = this.props
        const { dataSource, columns, loading, newKeyVisible, newKeyValue, userList, total } = this.state

        return (
            <div>
                <Card
                    title="API Key 管理"
                    bordered={false}
                >
                    {newKeyVisible && newKeyValue && (
                        <Alert
                            type="success"
                            showIcon
                            closable
                            onClose={() => this.setState({ newKeyVisible: false })}
                            message="API Key 已生成，请立即保存完整 Key"
                            description={(
                                <div style={{ marginTop: 8 }}>
                                    <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 4, marginBottom: 12 }}>
                                        <Text copyable code style={{ wordBreak: 'break-all' }}>{newKeyValue}</Text>
                                    </div>
                                    <Space style={{ marginBottom: 12 }}>
                                        <Button type="primary" onClick={this.copyFullKey}>复制完整 Key</Button>
                                        <Button onClick={() => this.setState({ newKeyVisible: false })}>收起提示</Button>
                                    </Space>
                                </div>
                            )}
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {/* 管理员模式显示用户筛选 */}
                    {!onlyMine && (
                        <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
                            <Select
                                style={{ width: 220 }}
                                showSearch
                                allowClear
                                placeholder="按用户筛选"
                                optionFilterProp="children"
                                fieldNames={{ label: 'USER_NICK', value: 'USER_ID' }}
                                filterOption={(input, option) =>
                                    ((option?.USER_NICK ?? '') + (option?.pinyin ?? '')).toLowerCase().includes(input.toLowerCase())
                                }
                                options={userList}
                                onChange={(val) => {
                                    this.setState(
                                        { formParam: { ...this.state.formParam, userId: val, currentPage: 1 } },
                                        this.initList
                                    )
                                }}
                            />
                            <Button onClick={this.initList}>刷新</Button>
                        </div>
                    )}

                    <Table
                        bordered
                        size="small"
                        rowKey="id"
                        dataSource={dataSource}
                        columns={columns}
                        loading={loading}
                        scroll={{ x: onlyMine ? 1420 : 1600 }}
                        pagination={{
                            size: 'small',
                            current: this.state.formParam.currentPage,
                            pageSize: this.state.formParam.pageSize,
                            total,
                            showSizeChanger: true,
                            onChange: (page, pageSize) => {
                                this.setState(
                                    { formParam: { ...this.state.formParam, currentPage: page, pageSize } },
                                    this.initList
                                )
                            },
                        }}
                    />
                    {this.renderEnvironmentInstallerSection(this.getAvailableFullKey())}
                </Card>
            </div>
        )
    }
}
